import { useSeoMeta } from '@unhead/react';
import { NostrSection } from '@/components/NostrSection';
import { Link } from 'react-router-dom';
import {
  Heart,
  Shield,
  Brain,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ArrowLeft,
  Lock,
  Handshake,
  Eye,
  Zap,
  MessageCircle,
  Users,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/* ── Data ────────────────────────────────────────────────────────────────── */

const calming = [
  {
    icon: Brain,
    title: 'Sensory Containment',
    description:
      'For some people, gentle restraint around the wrists and arms provides deep pressure input similar to a weighted blanket. This proprioceptive feedback can quiet an overactive nervous system and create a grounded, present-body feeling.',
  },
  {
    icon: Heart,
    title: 'Surrender & Release',
    description:
      'Physically not being able to act can paradoxically release the mental need to act. Handing over control — to a partner, to a device, to a situation — can be a form of trust that triggers deep relaxation.',
  },
  {
    icon: Shield,
    title: 'Defined Boundaries',
    description:
      'Restraint creates clear physical boundaries. For people who struggle with knowing where their body is in space, or who feel overwhelmed by open-ended situations, a defined physical limit can feel like a relief.',
  },
  {
    icon: Zap,
    title: 'Heightened Presence',
    description:
      'Being bound focuses attention on the body and the current moment. Worries about tasks, responsibilities, or the future recede because the immediate physical experience takes precedence.',
  },
  {
    icon: Lock,
    title: 'Trust & Intimacy',
    description:
      'Consensual restraint between partners requires — and reinforces — deep mutual trust. The act of allowing yourself to be bound is an act of vulnerability; holding someone safely is an act of care.',
  },
  {
    icon: Sparkles,
    title: 'Ritual & Structure',
    description:
      'The process of being restrained — or restraining — can function as a meaningful ritual that marks the beginning of a period of focus, play, or intimacy. Structure itself can be comforting.',
  },
];

const examples = [
  {
    icon: Shield,
    category: 'Law Enforcement',
    title: 'Police & Handcuffs',
    desc: 'Police officers routinely handcuff suspects with their hands behind their back as a standard detainment technique. The position maximises the officer\'s control while minimising the risk of the detained person reaching for weapons or resisting. Rigid handcuffs or zip-ties hold the wrists close together at the small of the back. In professional training, officers learn to apply them firmly but without excessive tightness — checking for two-finger clearance to prevent nerve damage or vascular impairment. Being handcuffed behind the back is designed to be immobilising without being permanently harmful when applied correctly.',
    color: 'from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  {
    icon: Users,
    category: 'Consensual Bondage',
    title: 'BDSM & Kink Communities',
    desc: 'Within consenting adult relationships and the broader kink community, tying a partner\'s hands behind their back is one of the most common forms of bondage. Rope, leather cuffs, and soft ties are used with great care. Experienced practitioners learn safe tie patterns that avoid pressure on the radial nerve (which runs along the outer wrist), maintain circulation, and allow rapid release. The practice is governed by the principles of safe, sane, and consensual (SSC) or risk-aware consensual kink (RACK).',
    color: 'from-rose-50 to-rose-100 dark:from-rose-950/30 dark:to-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
  },
  {
    icon: Handshake,
    category: 'Therapeutic Contexts',
    title: 'Occupational & Sensory Therapy',
    desc: 'Occupational therapists sometimes use weighted garments, compression vests, or guided restraint activities to provide deep-pressure proprioceptive input. For autistic individuals, people with sensory processing differences, or those with high anxiety, controlled restriction of limb movement can be part of a broader sensory diet. This is always done with the explicit cooperation and consent of the person.',
    color: 'from-teal-50 to-teal-100 dark:from-teal-950/30 dark:to-teal-900/20',
    border: 'border-teal-200 dark:border-teal-800',
  },
  {
    icon: Eye,
    category: 'Escape Rooms & Performance',
    title: 'Escape Rooms & Stage Magic',
    desc: 'Escape room designers and stage magicians regularly incorporate rope or cuff restraint as an experience element. In escape rooms, being "tied up" (often with easy releases) adds theatrical tension. In stage magic, the performer\'s escape from bound hands behind the back is a classic spectacle — a demonstration of flexibility, technique, and showmanship.',
    color: 'from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
  },
];

const precautions = [
  {
    title: 'Check Circulation Regularly',
    desc: 'Ensure hands and fingers remain warm and retain normal colour. Tingling, numbness, or a pins-and-needles sensation is a signal to release or loosen immediately. Aim for two-finger clearance between any restraint and the skin.',
  },
  {
    title: 'Avoid Nerve Pressure Points',
    desc: 'The radial nerve runs along the outer wrist and the inner upper arm. Restraints that press directly on these areas can cause temporary or — with prolonged pressure — lasting nerve damage. Use soft materials and proper positioning away from bony prominences.',
  },
  {
    title: 'Establish a Clear Release Signal',
    desc: 'Agree in advance on a verbal safeword and a non-verbal signal (e.g. a tap pattern or dropping an object) for situations where speech is difficult. The release signal must always be honoured, immediately and without question.',
  },
  {
    title: 'Keep a Safety Cutter Nearby',
    desc: 'Whenever rope or non-quick-release restraints are used, a pair of trauma shears or EMT scissors should be within arm\'s reach of the person who is not bound. Never use restraints you cannot rapidly remove.',
  },
  {
    title: 'Limit Duration',
    desc: 'Even comfortable restraint restricts blood flow and fatigues muscles. Begin with short sessions and gradually extend only as both comfort and physiology allow. Shoulders, particularly with arms behind the back, can develop strain surprisingly quickly.',
  },
  {
    title: 'Do Not Restrain Alone',
    desc: 'Being bound behind the back while alone removes the ability to respond to emergencies — a fall, a fire, a medical event. Restraint behind the back should always involve another person who can provide immediate assistance.',
  },
  {
    title: 'Aftercare Matters',
    desc: 'After any restraint experience, take time for aftercare: gentle movement, warmth, hydration, and emotional check-in. Adrenaline and endorphins can mask discomfort during the experience; physical and emotional processing happens afterwards.',
  },
  {
    title: 'Consent Is Ongoing',
    desc: 'Agreement given before a session can be withdrawn at any point during it. No prior arrangement, dynamic, or relationship overrides the immediate wish of either person to stop. Consent is not a one-time gate but a continuous conversation.',
  },
];

const challenges = [
  {
    level: 'Beginner',
    title: 'Soft Cuffs, Five Minutes',
    desc: 'Using padded wrist cuffs or a soft scarf, have a trusted person hold your hands lightly behind your back for five minutes. Focus on your breathing and notice how your body responds.',
  },
  {
    level: 'Beginner',
    title: 'Crossed Wrists Hold',
    desc: 'Cross your wrists at the small of your back and simply hold them there with your own hands for ten minutes — no external restraint. Observe the sense of stillness it creates.',
  },
  {
    level: 'Intermediate',
    title: 'Rope Exploration Session',
    desc: 'With a partner who has learned basic safe tie techniques, try a simple single-column wrist tie behind the back for fifteen minutes. Use a safeword and check circulation every five minutes.',
  },
  {
    level: 'Intermediate',
    title: 'Mindfulness Bound',
    desc: 'While lightly bound, guide yourself through a ten-minute body-scan meditation. Notice each physical sensation without trying to change it. How does restriction shift your awareness?',
  },
  {
    level: 'Advanced',
    title: 'Thirty-Minute Bound Walk',
    desc: 'With a trusted companion guiding you, take a slow walk of thirty minutes with your hands secured behind your back. Focus on balance, gait, and trust.',
  },
  {
    level: 'Advanced',
    title: 'Bound Stillness',
    desc: 'Remain bound in a comfortable, supported position for forty-five minutes while listening to music or a spoken-word recording. Practice releasing the impulse to move.',
  },
];

const neurodivergent = [
  {
    heading: 'Why Some ND People Find It Calming',
    content:
      'Autistic people, ADHDers, and others with sensory processing differences often benefit from deep pressure and proprioceptive input — information about where the body is in space. Having hands restrained behind the back provides constant, predictable pressure that can reduce sensory "noise." For people who experience their body as overwhelming or unclear, the defined physical boundaries of restraint can paradoxically feel liberating. The reduction of hand movement also removes a source of visual distraction.',
  },
  {
    heading: 'Stimming, Restriction, and Self-Regulation',
    content:
      'Many autistic people stim — engage in repetitive movements — to self-regulate arousal and emotion. Some find that gentle restraint serves a similar function: it occupies the proprioceptive channel without requiring active movement. Rather than needing to flap, squeeze, or rock, the steady pressure of restraint provides an ongoing low-level input. This is deeply individual; what works powerfully for one person may feel intolerable for another, and both responses are valid.',
  },
  {
    heading: 'When Restriction Feels Threatening',
    content:
      'For other neurodivergent people — particularly those with trauma histories, sensory hyperreactivity, or a strong need for autonomy and escape routes — physical restraint can be deeply distressing. The loss of control over one\'s body, even if consensual in principle, may trigger freeze, panic, or dissociation. This is not a failure; it is the nervous system doing exactly what it is designed to do. These responses deserve the same respect and accommodation as any other sensory need.',
  },
  {
    heading: 'Autonomy and Self-Knowledge',
    content:
      'Whether you find hands-behind-back restraint calming, neutral, aversive, or exciting, that response is yours. Neurodivergent people are often told their sensory experiences are wrong or need to be corrected. On this subpage and everywhere else, the principle is simple: you are the expert on your own body. Explore carefully, communicate clearly, and honour whatever you find — including the right to never explore it at all.',
  },
];

const levelColour: Record<string, string> = {
  Beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Advanced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

/* ── Component ───────────────────────────────────────────────────────────── */

export default function BoundPage() {
  useSeoMeta({
    title: 'Hands Bound Behind Your Back — Exploration, Safety & Wellbeing',
    description:
      'A thoughtful look at hands bound behind the back: calming experiences, real-world contexts, safety precautions, and neurodivergent perspectives.',
    robots: 'noindex, nofollow',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to main page
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="#calming" className="text-muted-foreground hover:text-foreground transition-colors">Calming</a>
            <a href="#examples" className="text-muted-foreground hover:text-foreground transition-colors">Examples</a>
            <a href="#precautions" className="text-muted-foreground hover:text-foreground transition-colors">Safety</a>
            <a href="#challenges" className="text-muted-foreground hover:text-foreground transition-colors">Challenges</a>
            <a href="#neurodivergent" className="text-muted-foreground hover:text-foreground transition-colors">ND</a>
            <a href="#nostr" className="text-muted-foreground hover:text-foreground transition-colors">Nostr</a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-14 bg-gradient-to-br from-rose-950 via-gray-950 to-purple-950">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #be185d 0%, transparent 60%), radial-gradient(circle at 70% 50%, #7c3aed 0%, transparent 60%)' }}
        />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white/90 text-sm mb-6">
            <Lock className="h-4 w-4" />
            Consent · Safety · Self-knowledge
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight mb-6 drop-shadow-lg">
            Hands Bound<br />Behind Your Back
          </h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-8">
            A thoughtful exploration of having your hands restrained behind your back — when it can feel wonderful, where it appears in the real world, and how to do it safely.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="#calming"
              className="inline-flex items-center gap-2 bg-white text-gray-900 font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors shadow-lg"
            >
              Explore
            </a>
            <a
              href="#precautions"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold px-6 py-3 rounded-full hover:bg-white/20 transition-colors"
            >
              Safety First
            </a>
          </div>
        </div>

        <a
          href="#calming"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown className="h-7 w-7" />
        </a>
      </section>

      {/* CALMING SECTION */}
      <section id="calming" className="py-20 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-sm font-medium mb-4">
              Why it can feel good
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              When Restraint Is Calming
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              For many people, having their hands bound behind their back is not distressing — it is grounding, releasing, or even joyful. Here is why.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {calming.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="border hover:shadow-lg transition-shadow group">
                <CardContent className="pt-6 pb-6">
                  <div className="w-11 h-11 rounded-xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mb-4 group-hover:bg-rose-200 dark:group-hover:bg-rose-900/50 transition-colors">
                    <Icon className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLES SECTION */}
      <section id="examples" className="py-20 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-4">
              In the real world
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Where Hands Bound Behind the Back Appears
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From law enforcement to consensual play, this position appears across many different contexts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {examples.map(({ icon: Icon, category, title, desc, color, border }) => (
              <Card key={title} className={`bg-gradient-to-br ${color} ${border} border`}>
                <CardContent className="pt-6 pb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-background/50 flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{category}</span>
                      <h3 className="font-bold text-lg mt-0.5 mb-2">{title}</h3>
                      <p className="text-sm leading-relaxed text-foreground/80">{desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRECAUTIONS */}
      <section id="precautions" className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-sm font-medium mb-4">
              Safety first
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <AlertTriangle className="h-8 w-8 text-yellow-500" />
              Precautions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Restraint behind the back can be a wonderful experience — but only with proper care, communication, and preparation.
            </p>
          </div>

          <div className="space-y-4">
            {precautions.map(({ title, desc }) => (
              <Card key={title} className="hover:shadow-md transition-shadow border-l-4 border-l-yellow-400 dark:border-l-yellow-600">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section id="challenges" className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium mb-4">
              Try it yourself
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Challenges</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Progressive experiences to explore with care, curiosity, and a trusted person by your side.
              Always read the precautions above before beginning.
            </p>
          </div>

          <div className="space-y-4">
            {challenges.map(({ level, title, desc }) => (
              <Card key={title} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 className="h-5 w-5 text-muted-foreground/50 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h3 className="font-semibold">{title}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${levelColour[level]}`}>
                          {level}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* NEURODIVERGENT */}
      <section id="neurodivergent" className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-sm font-medium mb-4">
              Neurodivergent perspectives
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <Brain className="h-8 w-8 text-teal-500" />
              Neurodivergence & Bound Hands
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Autistic people, ADHDers, and others with sensory processing differences can experience restraint in very different — and equally valid — ways.
            </p>
          </div>

          <div className="space-y-6">
            {neurodivergent.map(({ heading, content }) => (
              <Card key={heading} className="border hover:shadow-md transition-shadow">
                <CardContent className="pt-6 pb-6">
                  <h3 className="font-bold text-lg mb-3 text-teal-700 dark:text-teal-300">{heading}</h3>
                  <p className="text-sm leading-relaxed text-foreground/80">{content}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-10 p-6 rounded-2xl bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800">
            <div className="flex items-start gap-4">
              <MessageCircle className="h-6 w-6 text-teal-600 dark:text-teal-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-teal-800 dark:text-teal-200 mb-1">A personal note</p>
                <p className="text-sm text-teal-700/80 dark:text-teal-300/80 leading-relaxed">
                  The author of this page is an Asperger autist who personally finds both keeping their hands behind their back and having them bound calming and grounding — part of a broader appreciation for restriction and structure. This page exists because that experience is rarely discussed, even within neurodivergent communities. Sharing it here is an invitation, not a prescription. Your experience is your own.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-20 bg-gradient-to-br from-rose-950 via-gray-950 to-purple-950 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <blockquote className="text-2xl md:text-3xl font-light leading-relaxed mb-6">
            "To be held is not the same as to be trapped. The difference lives entirely in trust."
          </blockquote>
          <p className="text-white/40 text-sm">— on consensual restraint</p>
        </div>
      </section>

      {/* NOSTR SECTION — CW-only */}
      <NostrSection contentWarningOnly />

      {/* FOOTER */}
      <footer className="py-10 border-t border-border/50 bg-muted/20">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Link to="/" className="flex items-center gap-1 hover:text-foreground transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Hands Behind Your Back!
          </Link>
          <a
            href="https://marlowe.shakespeare.wtf"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            Vibed with Marlowe ✦
          </a>
        </div>
      </footer>
    </div>
  );
}
